<template>
  <div class="w-full">
    <div class="templates__list-search">
      <fluent-icon icon="search" class="search-icon" size="16" />
      <input
        ref="search"
        v-model="query"
        type="search"
        :placeholder="$t('EMAIL_TEMPLATES.MODAL.SEARCH_PLACEHOLDER')"
        class="templates__search-input"
      />
    </div>
    <div class="template__list-container">
      <div v-if="isLoading" class="template__loading">
        <span>{{ $t('EMAIL_TEMPLATES.LOADING') }}</span>
      </div>
      <template v-else>
        <div v-for="(template, i) in filteredTemplates" :key="template.id">
          <button
            class="template__list-item"
            @click="$emit('onSelect', template)"
          >
            <div class="template__list-item-inner">
              <div class="flex-between">
                <p class="label-title">{{ template.name }}</p>
                <span
                  class="inline-block py-1 px-2 rounded-sm text-xs leading-none cursor-default bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
                >
                  {{ template.visibility === 'global' ? $t('EMAIL_TEMPLATES.VISIBILITY.PUBLIC') : $t('EMAIL_TEMPLATES.VISIBILITY.PRIVATE') }}
                </span>
              </div>
              <div>
                <p class="strong">{{ $t('EMAIL_TEMPLATES.MODAL.SUBJECT_LABEL') }}</p>
                <p class="label-body">{{ template.subject }}</p>
              </div>
              <div v-if="bodySnippet(template)" class="template-snippet-wrap">
                <p class="strong">{{ $t('EMAIL_TEMPLATES.MODAL.BODY_SNIPPET_LABEL') }}</p>
                <p class="label-body template-snippet">{{ bodySnippet(template) }}</p>
              </div>
              <div
                v-if="template.attachments && template.attachments.length"
                class="template-attachments"
              >
                <fluent-icon icon="attach" size="14" class="attach-icon" />
                <span class="attachment-meta">
                  {{
                    template.attachments.length === 1
                      ? template.attachments[0].filename
                      : $t('EMAIL_TEMPLATES.MODAL.ATTACHMENTS_COUNT', {
                          count: template.attachments.length,
                        })
                  }}
                </span>
              </div>
            </div>
          </button>
          <hr v-if="i !== filteredTemplates.length - 1" :key="`hr-${i}`" />
        </div>
        <div v-if="!filteredTemplates.length">
          <p>{{ $t('EMAIL_TEMPLATES.MODAL.NO_TEMPLATES_FOUND') }} <strong>{{ query }}</strong></p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';

export default {
  data() {
    return {
      query: '',
      templates: [],
      isLoading: false,
    };
  },
  computed: {
    filteredTemplates() {
      const q = this.query.toLowerCase();
      if (!q) return this.templates;
      return this.templates.filter(t => {
        const name = (t.name || '').toLowerCase();
        const subject = (t.subject || '').toLowerCase();
        const body = this.plainBodyText(t.body || '').toLowerCase();
        return name.includes(q) || subject.includes(q) || body.includes(q);
      });
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    plainBodyText(html) {
      if (!html) return '';
      return String(html)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    bodySnippet(template) {
      const text = this.plainBodyText(template.body || '');
      if (!text) return '';
      return text.length > 120 ? `${text.slice(0, 117)}…` : text;
    },
    async fetchTemplates() {
      this.isLoading = true;
      try {
        const { data } = await EmailTemplatesAPI.get();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        this.templates = list;
      } catch (e) {
        this.templates = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.flex-between {
  @apply flex justify-between mb-2.5;
}

.templates__list-search {
  @apply items-center flex bg-slate-25 dark:bg-slate-900 mb-2.5 py-0 px-2.5 rounded-md border border-solid border-slate-100 dark:border-slate-700;

  .search-icon {
    @apply text-slate-400 dark:text-slate-300;
  }

  .templates__search-input {
    @apply bg-transparent border-0 text-xs h-auto m-0;
  }
}

.template__list-container {
  @apply bg-slate-25 dark:bg-slate-900 rounded-md max-h-[18.75rem] overflow-y-auto p-2.5;

  .template__list-item {
    @apply rounded-lg cursor-pointer block p-2.5 text-left w-full hover:bg-woot-50 dark:hover:bg-slate-800;

    .label-title {
      @apply text-sm font-semibold;
    }

    .label-body {
      @apply text-xs text-slate-600 dark:text-slate-400 truncate max-w-xs;
    }
  }

  .template-snippet-wrap {
    @apply mt-2;
  }

  .template-snippet {
    @apply max-w-full line-clamp-2 whitespace-normal;
  }

  .template-attachments {
    @apply flex items-center gap-1.5 mt-2 text-xs text-slate-600 dark:text-slate-400;

    .attach-icon {
      @apply flex-shrink-0 text-slate-500 dark:text-slate-400;
    }

    .attachment-meta {
      @apply truncate;
    }
  }
}

.template__loading {
  @apply flex items-center justify-center p-4 text-sm text-slate-500;
}

.strong {
  @apply text-xs font-semibold;
}

hr {
  @apply border-b border-solid border-slate-100 dark:border-slate-700 my-2.5 mx-auto max-w-[95%];
}
</style>
